import React, { useRef, useState } from 'react';

interface MagnetProps {
  children: React.ReactNode;
  padding?: number;
  disabled?: boolean;
  magnetStrength?: number;
  activeTransition?: string;
  inactiveTransition?: string;
  wrapperClassName?: string;
  innerClassName?: string;
}

const Magnet: React.FC<MagnetProps> = ({
  children,
  padding = 80,
  disabled = false,
  magnetStrength = 3,
  activeTransition = 'transform 0.2s ease-out',
  inactiveTransition = 'transform 0.5s ease-in-out',
  wrapperClassName = '',
  innerClassName = '',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [, setActive] = useState(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxDist = Math.max(rect.width, rect.height) / 2 + padding;

    if (dist < maxDist) {
      setActive(true);
      const strength = 1 - dist / maxDist;
      const tx = (dx * strength) / magnetStrength;
      const ty = (dy * strength) / magnetStrength;
      (el.firstElementChild as HTMLElement).style.transform = `translate(${tx}px, ${ty}px)`;
      (el.firstElementChild as HTMLElement).style.transition = activeTransition;
    } else {
      setActive(false);
      (el.firstElementChild as HTMLElement).style.transform = 'translate(0,0)';
      (el.firstElementChild as HTMLElement).style.transition = inactiveTransition;
    }
  };

  const handleMouseLeave = () => {
    const el = ref.current;
    if (!el) return;
    setActive(false);
    (el.firstElementChild as HTMLElement).style.transform = 'translate(0,0)';
    (el.firstElementChild as HTMLElement).style.transition = inactiveTransition;
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`inline-block ${wrapperClassName}`}
    >
      <div className={`${innerClassName}`}>
        {children}
      </div>
    </div>
  );
};

export default Magnet;
